/* Public cross-tenant shops directory for `/shops`.
 * Uses global `/api/v1/public/customer-sales-points` (no tenant ApiClient).
 */

import { ApiError } from '../../shared/api/ApiClient.js';
import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { platformJsonHeaders } from '../../shared/http/platformEnvelopeLocale.js';
import {
  rememberListableTenantCodesFromSalesPoints,
  rememberPublicCustomerListableSalesPoints,
} from '../shop/customerDiscoveryListability.js';
import {
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
  resolveConsumerSquareLogoUrl,
  type LocalizedNameMap,
  type LogoChipBackgroundSettings,
  type LogoChipRimSettings,
} from 'pi-kiosk-shared';
import {
  isOnlineOnlyCustomerPresence,
  parseSalesPointCustomerPresence,
  type SalesPointCustomerPresence,
} from './customerPresence.js';

/** @deprecated Prefer {@link SalesPointCustomerPresence} from `./customerPresence.js`. */
export type PublicCustomerSalesPointPresence = SalesPointCustomerPresence;

export {
  isOnlineOnlyCustomerPresence,
  parseSalesPointCustomerPresence,
  type SalesPointCustomerPresence,
} from './customerPresence.js';

export interface PublicCustomerSalesPointDTO extends LogoChipRimSettings, LogoChipBackgroundSettings {
  salesPointId: number;
  code: string;
  name: string;
  /** Per-locale display-name overrides; null/omit = use `name`. */
  nameLocales?: LocalizedNameMap | null;
  /** Universal sales-point description fallback text. */
  description?: string | null;
  /** Per-locale description overrides; null/omit = use `description`. */
  descriptionLocales?: LocalizedNameMap | null;
  location: string | null;
  /** WGS84 latitude from API; null when unset. */
  lat: number | null;
  /** WGS84 longitude from API; null when unset. */
  lng: number | null;
  /**
   * Explicit storefront presence from wire (A15). ONLINE_ONLY ⇒ public location/geo null
   * (A13a plumbing — BE Exit owned by wire-contract).
   */
  customerPresence: SalesPointCustomerPresence;
  /**
   * Listability-aligned Oblíbené headcount from SQL (`COALESCE(fav.cnt,0)`).
   * Required on every public directory row.
   */
  favoriteCount: number;
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  customerShopUrl: string;
  /** Signed SP image stream URL, or null when absent. */
  imageUrl: string | null;
  /** Optional dark-regime SP image; falls back to {@link imageUrl}. */
  imageUrlDark: string | null;
  /** Signed tenant square logo stream URL, or null when absent / applyToCustomerPwa off. */
  tenantLogoUrl: string | null;
  /** Optional dark-regime tenant square logo; falls back to {@link tenantLogoUrl}. */
  tenantLogoUrlDark: string | null;
  /** When false, platform shop list hides tenant logo overlay on store-photo thumbs. */
  showTenantLogoOnStorePhoto: boolean;
}

/** Normalize a public directory row — rim fields always present (defaults when absent). */
export function normalizePublicCustomerSalesPointItem(
  raw: PublicCustomerSalesPointDTO,
): PublicCustomerSalesPointDTO {
  const rim = normalizeLogoChipRimSettings(raw);
  const background = normalizeLogoChipBackgroundSettings(raw);
  const customerPresence = parseSalesPointCustomerPresence(raw.customerPresence);
  const onlineOnly = isOnlineOnlyCustomerPresence(customerPresence);
  return {
    ...raw,
    customerPresence,
    // A13a plumbing — force-null public location+geo for ONLINE_ONLY (preserve BE nulls; never rehydrate ops/geo).
    ...(onlineOnly
      ? { location: null as string | null, lat: null as number | null, lng: null as number | null }
      : {}),
    description: typeof raw.description === 'string' ? raw.description : null,
    descriptionLocales:
      raw.descriptionLocales !== undefined && raw.descriptionLocales !== null
        ? raw.descriptionLocales
        : null,
    imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : null,
    imageUrlDark: resolveConsumerSquareLogoUrl(raw.imageUrlDark),
    tenantLogoUrl: resolveConsumerSquareLogoUrl(raw.tenantLogoUrl),
    tenantLogoUrlDark: resolveConsumerSquareLogoUrl(raw.tenantLogoUrlDark),
    showLogoChipRimLight: rim.showLogoChipRimLight,
    showLogoChipRimDark: rim.showLogoChipRimDark,
    logoChipRimColorLight: rim.logoChipRimColorLight,
    logoChipRimColorDark: rim.logoChipRimColorDark,
    showLogoChipBackgroundLight: background.showLogoChipBackgroundLight,
    showLogoChipBackgroundDark: background.showLogoChipBackgroundDark,
    logoChipBackgroundColorLight: background.logoChipBackgroundColorLight,
    logoChipBackgroundColorDark: background.logoChipBackgroundColorDark,
    showTenantLogoOnStorePhoto:
      typeof raw.showTenantLogoOnStorePhoto === 'boolean' ? raw.showTenantLogoOnStorePhoto : true,
  };
}

export interface PublicCustomerSalesPointsPage {
  items: PublicCustomerSalesPointDTO[];
  nextCursor: string | null;
}

interface PublicCustomerSalesPointsEnvelope {
  success: true;
  data: PublicCustomerSalesPointsPage;
}

interface PublicCustomerSalesPointsErrorEnvelope {
  success: false;
  error: string;
  code?: string;
}

export async function fetchPublicCustomerSalesPoints(
  apiBaseUrl: string,
  options?: {
    cursor?: string | null;
    limit?: number;
    tenantCode?: string | null;
    /** Only `favoriteCountDesc` — omit for nameAsc/nameDesc/orgAsc/distanceAsc. */
    sort?: 'favoriteCountDesc' | null;
    signal?: AbortSignal;
  },
): Promise<PublicCustomerSalesPointsPage> {
  const base = apiBaseUrl.replace(/\/+$/, '');
  const params = new URLSearchParams();
  const cursor = options?.cursor;
  if (typeof cursor === 'string' && cursor.trim().length > 0) {
    params.set('cursor', cursor.trim());
  }
  if (options?.limit !== undefined) {
    params.set('limit', String(options.limit));
  }
  const tenantCode = options?.tenantCode;
  if (typeof tenantCode === 'string' && tenantCode.trim().length > 0) {
    params.set('tenantCode', tenantCode.trim());
  }
  // Never leave favoriteCountDesc on the wire for non-popularity FE sorts.
  if (options?.sort === 'favoriteCountDesc') {
    params.set('sort', 'favoriteCountDesc');
  }
  const qs = params.toString();
  const url = `${base}/api/v1/public/customer-sales-points${qs.length > 0 ? `?${qs}` : ''}`;

  const response = await stormAwareFetch(url, {
    method: 'GET',
    headers: platformJsonHeaders(),
    // Avoid stale imageUrl/tenantLogoUrl (galleryVersion) from HTTP cache after branding upload.
    cache: 'no-store',
    signal: options?.signal,
  });

  let body:
    | PublicCustomerSalesPointsEnvelope
    | PublicCustomerSalesPointsErrorEnvelope;
  try {
    body = (await response.json()) as
      | PublicCustomerSalesPointsEnvelope
      | PublicCustomerSalesPointsErrorEnvelope;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }

  if (!response.ok || body.success !== true) {
    throw customerEnvelopeApiError(response.status, body);
  }

  const items = body.data.items.map((item) => {
    // Required DTO field (backend always sends SQL COALESCE count) — never soften to 0.
    if (typeof item.favoriteCount !== 'number' || !Number.isFinite(item.favoriteCount)) {
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: response.status,
          code: 'MISSING_DATA',
        }),
        response.status,
        'MISSING_DATA',
      );
    }
    // G2 — dual-regime logo-chip rim always present (defaults when wire omits/invalid).
    return normalizePublicCustomerSalesPointItem(item);
  });

  // G1 — SP fetch unions positive listable evidence only (platform pages + tenant-scoped).
  // Unseen tenants stay unknown (null), not false. Empty pages are no-op.
  rememberListableTenantCodesFromSalesPoints(items);

  return { items, nextCursor: body.data.nextCursor };
}

/** SP allowlist from Obchody public directory (same SoT as `/shops`). */
export interface PublicCustomerSalesPointListableAllowlist {
  readonly salesPointIds: readonly number[];
  readonly tenantSlugKeys: readonly string[];
}

const LISTABLE_ALLOWLIST_PAGE_LIMIT = 100;
/** Safety cap — directory is expected to be far smaller; prevents runaway paging. */
export const LISTABLE_ALLOWLIST_MAX_PAGES = 50;

/** Thrown when page drain hits the safety cap with more rows remaining (never silent truncate). */
export const LISTABLE_ALLOWLIST_INCOMPLETE_CODE = 'LISTABLE_ALLOWLIST_INCOMPLETE';

function tenantSlugKey(tenantCode: string, code: string): string {
  return `${tenantCode.trim().toLowerCase()}|${code.trim().toLowerCase()}`;
}

/**
 * Drain unscoped public customer-sales-points pages into an SP allowlist.
 * Guest Favorites / Purchases must intersect against this — never tenant discovery alone.
 *
 * Prefer complete drain. If `maxPages` is exhausted while `nextCursor` remains, throws
 * `ApiError` with code `LISTABLE_ALLOWLIST_INCOMPLETE` (fail closed upstream) — never
 * silently omit Obchody-listable shops from Favorites.
 */
export async function fetchPublicCustomerSalesPointListableAllowlist(
  apiBaseUrl: string,
  options?: {
    readonly signal?: AbortSignal;
    /** Override safety cap (tests). Defaults to {@link LISTABLE_ALLOWLIST_MAX_PAGES}. */
    readonly maxPages?: number;
    /**
     * G8 — each drained page (full DTO incl. logo/rim) for pending-favourite
     * branding merge without a second directory round-trip.
     */
    readonly onPageItems?: (items: readonly PublicCustomerSalesPointDTO[]) => void;
  },
): Promise<PublicCustomerSalesPointListableAllowlist> {
  const maxPages =
    typeof options?.maxPages === 'number' &&
    Number.isFinite(options.maxPages) &&
    options.maxPages > 0
      ? Math.floor(options.maxPages)
      : LISTABLE_ALLOWLIST_MAX_PAGES;
  const salesPointIds: number[] = [];
  const tenantSlugKeys: string[] = [];
  const seenIds = new Set<number>();
  const seenSlugs = new Set<string>();
  let cursor: string | null = null;

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const page = await fetchPublicCustomerSalesPoints(apiBaseUrl, {
      cursor,
      limit: LISTABLE_ALLOWLIST_PAGE_LIMIT,
      signal: options?.signal,
    });
    options?.onPageItems?.(page.items);
    for (const row of page.items) {
      if (!seenIds.has(row.salesPointId)) {
        seenIds.add(row.salesPointId);
        salesPointIds.push(row.salesPointId);
      }
      const slugKey = tenantSlugKey(row.tenantCode, row.code);
      if (slugKey.length > 1 && !seenSlugs.has(slugKey)) {
        seenSlugs.add(slugKey);
        tenantSlugKeys.push(slugKey);
      }
    }
    if (page.nextCursor === null || page.nextCursor.length === 0) {
      // G8 — complete sync allowlist so Purchases lastKnown fail-closes like Favorites.
      rememberPublicCustomerListableSalesPoints({ salesPointIds, tenantSlugKeys });
      return { salesPointIds, tenantSlugKeys };
    }
    cursor = page.nextCursor;
  }

  throw new ApiError(
    resolvePublicApiFailureMessage({
      status: 500,
      code: LISTABLE_ALLOWLIST_INCOMPLETE_CODE,
    }),
    500,
    LISTABLE_ALLOWLIST_INCOMPLETE_CODE,
    { maxPages, pageLimit: LISTABLE_ALLOWLIST_PAGE_LIMIT, nextCursor: cursor },
  );
}

/**
 * Full-page drain of public customer-sales-points into DTO rows (D22 / with-q Obchody).
 * Fail-closed when the safety cap is hit with pages remaining — never silent page-1 under-match.
 * Wire params stay cursor|limit|tenantCode|sort only (no `q`/`name` on the API).
 */
export async function fetchPublicCustomerSalesPointsDrain(
  apiBaseUrl: string,
  options?: {
    readonly signal?: AbortSignal;
    readonly maxPages?: number;
    readonly tenantCode?: string | null;
    readonly sort?: 'favoriteCountDesc' | null;
  },
): Promise<readonly PublicCustomerSalesPointDTO[]> {
  const maxPages =
    typeof options?.maxPages === 'number' &&
    Number.isFinite(options.maxPages) &&
    options.maxPages > 0
      ? Math.floor(options.maxPages)
      : LISTABLE_ALLOWLIST_MAX_PAGES;
  const items: PublicCustomerSalesPointDTO[] = [];
  const seenIds = new Set<number>();
  let cursor: string | null = null;

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const page = await fetchPublicCustomerSalesPoints(apiBaseUrl, {
      cursor,
      limit: LISTABLE_ALLOWLIST_PAGE_LIMIT,
      tenantCode: options?.tenantCode,
      sort: options?.sort,
      signal: options?.signal,
    });
    for (const row of page.items) {
      if (!seenIds.has(row.salesPointId)) {
        seenIds.add(row.salesPointId);
        items.push(row);
      }
    }
    if (page.nextCursor === null || page.nextCursor.length === 0) {
      return items;
    }
    cursor = page.nextCursor;
  }

  throw new ApiError(
    resolvePublicApiFailureMessage({
      status: 500,
      code: LISTABLE_ALLOWLIST_INCOMPLETE_CODE,
    }),
    500,
    LISTABLE_ALLOWLIST_INCOMPLETE_CODE,
    { maxPages, pageLimit: LISTABLE_ALLOWLIST_PAGE_LIMIT, nextCursor: cursor },
  );
}
